import React, { useEffect } from 'react';
import Search from '@mui/icons-material/Search';
import { Controller, useForm } from 'react-hook-form';
import makeStyles from '@mui/styles/makeStyles';
import { Box, InputAdornment } from '@mui/material';
import { ProposalProduct } from '@trnsact/trnsact-shared-types/dist/generated';
import { ProductsFiltersDealer, ShowTypes } from '../../../types';
import { proposalProductsFiltersOptions } from '../../../constants';
import { FormRadioGroup, InputField, MultiSelect } from '../../../../../components/form';
import { SelectOption } from '../../../../../global';
import { proposalProductsFilterDealer } from 'modules/desking/lib';
import { useSelector } from 'react-redux';
import { getProductVendorName } from 'utils';

interface Props {
  products: ProposalProduct[];
  onFiltered: (filteredProducts: ProposalProduct[]) => void;
}

export const ProposalProductsFiltersDealer = ({ products, onFiltered }: Props) => {
  const classes = useStyles();

  const account = useSelector((state: any) => state.account);

  const { control, watch, setValue } = useForm<ProductsFiltersDealer>({
    defaultValues: {
      search: '',
      vendors: [],
      showOnly: ShowTypes.All,
    },
  });

  const vendorsOptions = products.reduce<SelectOption[]>((acc, product) => {
    const vendorName = getProductVendorName(product.aftermarketProduct, account.name);
    if (vendorName && !acc.map(option => option?.value).includes(vendorName)) {
      acc.push({ label: vendorName, value: vendorName });
    }

    return acc;
  }, []);

  useEffect(() => {
    const subscription = watch(value => {
      const filteredProducts = products.filter(product =>
        proposalProductsFilterDealer(product, value as ProductsFiltersDealer, account)
      );

      onFiltered(filteredProducts);
    });

    return subscription.unsubscribe;
  }, [watch, onFiltered, products]);

  const handleChangeVendors = (selected: (string | number)[]) => {
    const termsToString = selected.map(String);

    setValue('vendors', termsToString);
  };

  return (
    <Box className={classes.container}>
      <InputField
        control={control}
        name="search"
        label="Search"
        inputProps={{
          variant: 'outlined',
          placeholder: 'Enter value...',
          InputProps: {
            endAdornment: (
              <InputAdornment position="end">
                <Search />
              </InputAdornment>
            ),
          },
        }}
      />

      <Controller
        control={control}
        name="vendors"
        render={({ field }) => (
          <MultiSelect
            label="Aftermarket Product Vendors"
            options={vendorsOptions}
            formControlProps={{
              variant: 'standard',
              disabled: !vendorsOptions.length,
            }}
            {...field}
            onChange={handleChangeVendors}
          />
        )}
      />

      <Controller
        name="showOnly"
        control={control}
        render={({ field }) => (
          <FormRadioGroup row label="Show only" options={proposalProductsFiltersOptions} {...field} />
        )}
      />
    </Box>
  );
};

const useStyles = makeStyles({
  container: {
    gap: '1rem',
    display: 'flex',
    flexDirection: 'column',
  },
});
